import React, { useState, useEffect, useContext } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import {
  FaPhone, FaEnvelope, FaMapMarkerAlt, FaBars, FaTimes,
  FaChevronDown, FaSun, FaMoon, FaLinkedin, FaTwitter, FaFacebook,
  FaCode, FaGraduationCap, FaBullhorn, FaUsers, FaRobot,
  FaBuilding, FaShoppingCart, FaTruck, FaArrowRight
} from 'react-icons/fa';
import { ThemeContext } from '../context/ThemeContext';
import logo from '../assets/images/vkomak-logo.png';
import './Header.css';

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const { theme, toggleTheme } = useContext(ThemeContext);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const closeMenu = () => { setMenuOpen(false); setActiveDropdown(null); };

  const navItems = [
    { label: 'Home', path: '/', exact: true },
    { label: 'About', path: '/about' },
    {
      label: 'Solutions',
      path: '/solutions',
      dropdown: [
        { label: 'Software Development', path: '/solutions', icon: <FaCode /> },
        { label: 'AI & Automation', path: '/solutions#ai', icon: <FaRobot /> },
        { label: 'ERP & Enterprise', path: '/solutions#erp', icon: <FaBuilding /> },
        { label: 'E-Commerce', path: '/solutions#ecommerce', icon: <FaShoppingCart /> },
        { label: 'Logistics Systems', path: '/solutions#logistics', icon: <FaTruck /> },
      ]
    },
    {
      label: 'Divisions',
      dropdown: [
        { label: 'TalentX — Training', path: '/talentx', icon: <FaGraduationCap />, desc: 'Tech Training & Placement' },
        { label: 'PromoX — Marketing', path: '/promox', icon: <FaBullhorn />, desc: 'Digital Marketing & Growth' },
        { label: 'TalentForce — Staffing', path: '/talentforce', icon: <FaUsers />, desc: 'IT Staffing & Workforce' },
      ]
    },
    { label: 'Products', path: '/products' },
    { label: 'Portfolio', path: '/portfolio' },
    { label: 'Careers', path: '/careers' },
  ];

  return (
    <header className={`site-header${scrolled ? ' scrolled' : ''}`}>
      {/* Top Bar */}
      <div className="header-topbar">
        <Container fluid="xl">
          <div className="topbar-inner">
            <div className="topbar-left">
              <a href="tel:+914440066713" className="topbar-link">
                <FaPhone /> +91 44-4006 6713
              </a>
              <a href="mailto:contactus@vkomaktechnologies.com" className="topbar-link">
                <FaEnvelope /> contactus@vkomaktechnologies.com
              </a>
              <span className="topbar-link">
                <FaMapMarkerAlt /> Chennai, Tamil Nadu, India
              </span>
            </div>
            <div className="topbar-right">
              <a href="https://linkedin.com" target="_blank" rel="noreferrer" className="social-link"><FaLinkedin /></a>
              <a href="https://twitter.com" target="_blank" rel="noreferrer" className="social-link"><FaTwitter /></a>
              <a href="https://facebook.com" target="_blank" rel="noreferrer" className="social-link"><FaFacebook /></a>
              <button className="theme-toggle" onClick={toggleTheme} aria-label="Toggle theme">
                {theme === 'dark' ? <FaSun /> : <FaMoon />}
              </button>
            </div>
          </div>
        </Container>
      </div>

      {/* Main Nav */}
      <nav className="main-nav">
        <Container fluid="xl">
          <div className="nav-inner">
            <Link to="/" className="nav-brand" onClick={closeMenu}>
              <div className="brand-logo-clip">
                <img src={logo} alt="Vkomak Technologies" className="brand-logo" />
              </div>
            </Link>

            <ul className="nav-menu">
              {navItems.map((item, idx) => (
                <li
                  key={idx}
                  className={`nav-item${item.dropdown ? ' has-dropdown' : ''}`}
                  onMouseEnter={() => item.dropdown && setActiveDropdown(idx)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  {item.path ? (
                    <NavLink
                      to={item.path}
                      end={item.exact}
                      className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
                    >
                      {item.label}
                      {item.dropdown && <FaChevronDown className="chevron" />}
                    </NavLink>
                  ) : (
                    <span className="nav-link nav-link-btn">
                      {item.label}
                      <FaChevronDown className="chevron" />
                    </span>
                  )}

                  {item.dropdown && (
                    <div className={`dropdown-panel${activeDropdown === idx ? ' open' : ''}`}>
                      {item.dropdown.map((sub, si) => (
                        <Link key={si} to={sub.path} className="dropdown-item-vk" onClick={closeMenu}>
                          <span className="dropdown-icon">{sub.icon}</span>
                          <div>
                            <span className="dropdown-label">{sub.label}</span>
                            {sub.desc && <span className="dropdown-desc">{sub.desc}</span>}
                          </div>
                          <FaArrowRight className="dropdown-arrow" />
                        </Link>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>

            <div className="nav-actions">
              <Link to="/contact" className="btn-primary-vk btn-sm-nav">
                Get Started
              </Link>
              <button
                className="hamburger"
                onClick={() => setMenuOpen(!menuOpen)}
                aria-label="Toggle menu"
              >
                {menuOpen ? <FaTimes /> : <FaBars />}
              </button>
            </div>
          </div>
        </Container>
      </nav>

      {/* Mobile Menu Overlay */}
      <div className={`mobile-overlay${menuOpen ? ' open' : ''}`} onClick={closeMenu} />
      <div className={`mobile-menu${menuOpen ? ' open' : ''}`}>
        <div className="mobile-menu-header">
          <img src={logo} alt="Vkomak" className="brand-logo" />
          <button className="mobile-close" onClick={closeMenu}><FaTimes /></button>
        </div>
        <ul className="mobile-nav-list">
          {navItems.map((item, idx) => (
            <li key={idx} className="mobile-nav-item">
              {item.path ? (
                <NavLink to={item.path} end={item.exact} className="mobile-nav-link" onClick={closeMenu}>
                  {item.label}
                </NavLink>
              ) : (
                <button
                  className="mobile-nav-link mobile-nav-btn"
                  onClick={() => setActiveDropdown(activeDropdown === idx ? null : idx)}
                >
                  {item.label} <FaChevronDown className={`chevron${activeDropdown === idx ? ' rotated' : ''}`} />
                </button>
              )}
              {item.dropdown && activeDropdown === idx && (
                <ul className="mobile-sub-menu">
                  {item.dropdown.map((sub, si) => (
                    <li key={si}>
                      <Link to={sub.path} className="mobile-sub-link" onClick={closeMenu}>
                        {sub.icon} {sub.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
          <li>
            <Link to="/contact" className="btn-primary-vk mobile-cta" onClick={closeMenu}>
              Get Started
            </Link>
          </li>
        </ul>
        <div className="mobile-theme-toggle">
          <span>Switch Theme</span>
          <button className="theme-toggle" onClick={toggleTheme}>{theme === 'dark' ? <FaSun /> : <FaMoon />}</button>
        </div>
      </div>
    </header>
  );
};

export default Header;
